import React, { useEffect, useState } from 'react';
import { getAllPhoneNumber } from '../../utils/api/phoneNumberAPI';
import { getOutbondCall } from '../../utils/api/callAPI';
import { isVerifiedPhoneNumber, sendOTP, verifyOTP } from '../../utils/api/phoneVerificationAPI';
import { toast } from 'sonner';
import { Phone } from 'lucide-react';
import { useSelector } from 'react-redux';

export default function PlaceOutbondCall({ onClose, activeAssistantId }) {
  const COUNTRY_CODES = [
    { code: '91', label: '+91', name: 'India', phoneLength: 10 },
    { code: '971', label: '+971', name: 'UAE', phoneLength: 9 },
    { code: '1', label: '+1', name: 'USA', phoneLength: 10 },
  ];
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('91');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [numbers, setNumbers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);

  const [otp, setOtp] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [variableFields, setVariableFields] = useState({});
  const [variables, setVariables] = useState([]);
  const assistantDetails = useSelector((state) => state.assistants.activeDetail.details);
  const firstMessage = assistantDetails?.firstMessage || '';
  const promptMessage = assistantDetails?.model?.messages?.[0]?.content || '';
  const orgIsSubscribed = useSelector((state) => state?.orgData?.orgData?.organization?.isSubscribed)
  const selectedCountry = COUNTRY_CODES.find((country) => country.code === countryCode) || COUNTRY_CODES[0];
  const expectedPhoneLength = selectedCountry.phoneLength;
  const fullPhoneNumber = `+${countryCode}${phoneNumber}`;

  useEffect(() => {
    if (orgIsSubscribed) {
     
      setIsVerified(true);
      setStep(1); 
    }
  }, [orgIsSubscribed]);

    useEffect(() => {
      getAllPhoneNumber()
        .then((data) => {
          if (data?.phoneNumbers?.length > 0) {
            setNumbers(data.phoneNumbers);
            setPhoneNumberId(data.phoneNumbers[0].id);
          }
        })
        .catch(() => toast.error('Failed to fetch phone numbers'));
    }, []); 

  // useEffect(() => {
  //   // For now, use static number
  //   setNumbers([
  //     {
  //       id: '59090934-7b8b-45d6-9565-88a9cb6646fb',
  //       did: '+91-8071999799',
  //     }
  //   ]);
  //   setPhoneNumberId('59090934-7b8b-45d6-9565-88a9cb6646fb');
  // }, []);
  const extractVariables = (text) => {
    const matches = text.match(/{{\s*([\w\d_]+)\s*}}/g) || [];
    return matches.map((match) => match.replace(/{{\s*|\s*}}/g, ''));
  };
  useEffect(() => {
    const varsInFirst = extractVariables(firstMessage);
    const varsInPrompt = extractVariables(promptMessage);
    const uniqueVars = [...new Set([...varsInFirst, ...varsInPrompt])];
    setVariables(uniqueVars);

    // Initialize variable fields
    setVariableFields((prev) => {
      const updated = { ...prev };
      uniqueVars.forEach((key) => {
        if (!(key in updated)) {
          updated[key] = '';
        }
      });
      return updated;
    });
  }, [firstMessage, promptMessage]);
  const handleCheckVerification = async () => {
    const cleanedNumber = phoneNumber.trim();

    if (!new RegExp(`^\\d{${expectedPhoneLength}}$`).test(cleanedNumber)) {
      toast.error(`Enter a valid ${expectedPhoneLength}-digit phone number`);
      return;
    }

    try {
      setIsCheckingVerification(true);
      const response = await isVerifiedPhoneNumber(fullPhoneNumber);
      if (response?.verified) {
        setIsVerified(true);
        setStep(3);
      } else {
        setIsVerified(false);
        setIsSendingOTP(true);
        await sendOTP(`${countryCode}${cleanedNumber}`);
        toast.success('OTP sent successfully');
        setStep(2);
      }
    } catch (error) {
      toast.error(error?.message || 'Verification check failed');
    } finally {
      setIsCheckingVerification(false);
      setIsSendingOTP(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) {
      toast.error('Enter a valid 6-digit OTP');
      return;
    }

    try {
      setIsVerifyingOTP(true);
      const res = await verifyOTP(`${countryCode}${phoneNumber}`, otp);
      if (res?.message === 'Phone number verified successfully') {
        setIsVerified(true);
        toast.success('Phone verified successfully');
        setStep(3);
      } else {
        toast.error('Invalid OTP');
      }
    } catch (err) {
      toast.error(err?.message || 'OTP verification failed');
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const handleResendOTP = async () => {
    if (!phoneNumber) return;

    try {
      setIsSendingOTP(true);
      await sendOTP(`${countryCode}${phoneNumber}`);
      toast.success('OTP resent successfully');
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handlePlaceCall = async () => {
    if (!phoneNumber || !phoneNumberId) {
      toast.error('Please enter a valid phone number and select a calling number.');
      return;
    }

    if (!new RegExp(`^\\d{${expectedPhoneLength}}$`).test(phoneNumber)) {
      toast.error(`Please enter a valid ${expectedPhoneLength}-digit phone number.`);
      return;
    }

    for (const key of variables) {
      if (!variableFields[key]?.trim()) {
        toast.error(`Please fill the "${key}" field.`);
        return;
      }
    }

    const payload = {
      assistantId: activeAssistantId,
      assistantOverrides: {
        variableValues: variableFields,
      },
      phoneNumberId,
      customer: {
        number: fullPhoneNumber,
      },
    };

    try {
      setIsLoading(true);
      const response = await getOutbondCall(payload);
      toast.success(response?.messages || 'Call placed successfully');
      onClose();
    } catch (err) {
      console.error('Call failed:', err);
      toast.error(err?.message || 'Failed to place call. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (!phoneNumber.trim()) {
        toast.error('Please enter a phone number');
        return;
      }

      if (!new RegExp(`^\\d{${expectedPhoneLength}}$`).test(phoneNumber.trim())) {
        toast.error(`Please enter a valid ${expectedPhoneLength}-digit phone number`);
        return;
      }

      if (orgIsSubscribed) {
        setIsVerified(true);
        setStep(3);
      } else {
        handleCheckVerification();
      }

      return;
    }

    if (step === 2) {
      if (!otp.trim()) {
        toast.error('Please enter OTP');
        return;
      }
      handleVerifyOtp();
      return;
    }

    if (step === 3) {
      if (!phoneNumberId) {
        toast.error('Please select a calling number');
        return;
      }
      handlePlaceCall();
    }
  };



  const handlePhoneNumberInput = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= expectedPhoneLength) {
      setPhoneNumber(value);
    }
  };

  const handleOtpInput = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  const handleClose = () => {
    setStep(1);
    setPhoneNumber('');
    setCountryCode('91');
    setOtp('');
    setIsVerified(false);
    onClose();
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setOtp('');
    } else if (step === 3) {
      setStep(2);
    }
  };

  const totalSteps = orgIsSubscribed ? 2 : 3;

  const getStepTitle = () => {
    if (orgIsSubscribed) {
      return step === 1 ? 'Enter Phone Number' : 'Select Caller ID';
    }
  
    switch (step) {
      case 1: return 'Enter Phone Number';
      case 2: return 'Verify OTP';
      case 3: return 'Select Caller ID';
      default: return 'Place Outbound Call';
    }
  };
  

  const isProcessing = isLoading || isCheckingVerification || isVerifyingOTP || isSendingOTP;

  return (
    <div className="modal-view-container">
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '480px',
          minHeight: '420px',
          maxHeight: '90vh',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          className='flex justify-content-between align-items-center'
          style={{
            padding: '24px 24px 0 24px',
            marginBottom: '24px',
          }}
        >
          <div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
              <Phone size={17} style={{
                marginBottom: '8px',
                marginRight: '8px',
              }} />
              Place Outbound Call
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              {getStepTitle()} • Step {step} of {totalSteps}
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#9ca3af',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => e.target.style.color = '#374151'}
            onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
          >
            ×
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{ padding: '0 24px', marginBottom: '32px' }}>
          <div style={{
            width: '100%',
            height: '3px',
            backgroundColor: '#f3f4f6',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(step / totalSteps) * 100}%`,
              height: '100%',
              backgroundColor: '#3b82f6',
              transition: 'width 0.3s ease',
              borderRadius: '2px'
            }} />
          </div>
        </div>

        {/* Content */}
        <div style={{
          padding: '0 24px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Step 1: Phone number input */}
          {step === 1 && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Enter Phone Number
              </label>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                Enter a {expectedPhoneLength}-digit phone number for {selectedCountry.name}
              </div>
              <div
                className='flex gap-1'
                style={{ alignItems: 'center' }}
              >
                <select
                  value={countryCode}
                  onChange={(e) => {
                    setCountryCode(e.target.value);
                    setPhoneNumber('');
                    setOtp('');
                  }}
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    minWidth: '110px',
                    cursor: 'pointer',
                  }}
                >
                  {COUNTRY_CODES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label} ({country.name})
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={handlePhoneNumberInput}
                  placeholder={`Enter ${expectedPhoneLength}-digit number`}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              {/* Dynamic Variable Fields */}
              {variables.length > 0 && (
                <div style={{ marginBottom: '16px', marginTop: '10px' }}>
                  <label style={{ fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                    Fill in template variables:
                  </label>
                  {variables.map((key) => (
                    <div key={`var-${key}`} style={{ marginBottom: '10px' }}>
                      <input
                        type="text"
                        placeholder={`Enter ${key}`}
                        value={variableFields[key]}
                        onChange={(e) =>
                          setVariableFields({ ...variableFields, [key]: e.target.value })
                        }
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #ccc',
                          borderRadius: '6px',
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: OTP input */}
          {step === 2 && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Enter Verification Code
              </label>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                We've sent a 6-digit code to {fullPhoneNumber}
              </div>
              <div className='flex gap-2' style={{ marginBottom: '16px' }}>
                <input
                  type="text"
                  value={otp}
                  onChange={handleOtpInput}
                  placeholder="Enter 6-digit code"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
              <button
                onClick={handleResendOTP}
                disabled={isSendingOTP}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '12px',
                  cursor: isSendingOTP ? 'not-allowed' : 'pointer',
                  textDecoration: 'underline',
                  opacity: isSendingOTP ? 0.6 : 1,
                }}
              >
                {isSendingOTP ? 'Resending...' : 'Resend Code'}
              </button>
            </div>
          )}

          {/* Step 3: Number selector */}
          {step === 3 && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px',
                display: 'block'
              }}>
                Select Caller ID
              </label>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                Choose the number that will appear as the caller ID
              </div>
              <div style={{ position: 'relative' }}>
                <select
                  value={phoneNumberId}
                  onChange={(e) => setPhoneNumberId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 40px 12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    outline: 'none',
                    appearance: 'none',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                >
                  {numbers.length > 0 ? (
                    numbers.map((val, index) => (
                      <option key={index} value={val.id}>
                        {val.did}
                      </option>
                    ))
                  ) : (
                    <option disabled>No numbers available</option>
                  )}
                </select>
                {/* Mobile/Phone Icon */}
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '40%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6b7280',
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 10l5 5 5-5z" />
                  </svg>

                </div>
              </div>

              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '16px',
                lineHeight: '1.5'
              }}>
                Need more numbers? You can{' '}request phone numbers
                {/* <Link
                  to="#"
                  style={{
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  request phone numbers
                </Link> */}
                {' '}
                to start making calls from your custom numbers.
              </p>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div style={{
          padding: '24px',
          borderTop: '1px solid #f3f4f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px'
        }}>
          {step > 1 ? (
            <button
              onClick={handleBack}
              disabled={isProcessing}
              className='btn-back'
              style={{
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              Back
            </button>
          ) : <div />}

          <button
            onClick={handleNextStep}
            disabled={isProcessing}
            className='btn-primary'
            style={{
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '500',
              opacity: isProcessing ? 0.8 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '120px',
              justifyContent: 'center',
            }}
          >
            {isProcessing ? (
              <>
                <span className="spinner" />
                {isCheckingVerification ? 'Checking...' :
                  isVerifyingOTP ? 'Verifying...' :
                    isSendingOTP ? 'Sending...' :
                      isLoading ? 'Placing...' : 'Processing...'}
              </>
            ) : (
              step === 3 ? 'Place Call' : 'Next'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}